<template>
  <v-text-field
    class="a-text-field"
    ref="textField"
    :class="{
      dark: dark,
      solo: solo
    }"
    :disabled="disabled"
    :label="label"
    :placeholder="placeholder"
    :rules="rules"
    :hint="hint"
    :persistent-hint="persistentHint"
    :maxlength="maxLength"
    :value="formattedValue"
    :data-test-id="value"
    :readonly="readonly"
    :type="typeOfField"
    :suffix="suffix"
    :autofocus="autofocus"
    @update:error="handleError"
    @input="handleChange"
    @click.native="handleClick"
    @keyup.enter="handleEnter"
    @keypress="handleKeypress"
    @blur="handleBlur"
  >
    <template v-slot:append>
      <SvgIcon
        :class="{ 'warning-icon': warning }"
        :width="sizeIcon"
        :height="sizeIcon"
        v-if="error || warning"
        icon="Error"
      />
      <SvgIcon
        :width="sizeIcon"
        :height="sizeIcon"
        v-else-if="svgIcon && appendIcon"
        :icon="appendIcon"
      />
      <v-icon v-else-if="appendIcon" small color="grey">{{ appendIcon }}</v-icon>
      <slot name="appendIcon"></slot>
    </template>
    <template v-slot:prepend>
      <slot name="prependIcon"></slot>
    </template>
    <template v-slot:prepend-inner>
      <slot name="prependInnerIcon"></slot>
    </template>
  </v-text-field>
</template>

<script>
export default {
  name: 'ATextField',
  props: {
    fieldName: {
      type: String,
      default: ''
    },
    // theme
    dark: {
      type: Boolean,
      default: false
    },
    // style of input field
    solo: {
      type: Boolean,
      default: false
    },
    value: {},
    // Icon name: [asset.icons/ material icon's name]
    appendIcon: {
      type: String
    },
    suffix: {
      type: String,
      default: ''
    },
    typeOfField: {
      type: String
    },
    disabled: {
      type: Boolean
    },
    readonly: {
      type: Boolean
    },
    autofocus: {
      type: Boolean
    },
    warning: {
      type: Boolean
    },
    label: {
      type: String
    },
    placeholder: {
      type: String
    },
    // Use svgIcon library to inject file to inline html
    svgIcon: {
      type: Boolean
    },
    // Set input type: text, number
    type: {
      type: String,
      default: 'text'
    },
    // format value if type = "number"
    formatNumber: {
      type: Boolean,
      default: true
    },
    // Array function to validate value
    rules: {
      type: Array,
      default: () => []
    },
    maxLength: {
      type: String
    },
    onError: {
      type: Function
    },
    onChange: {
      type: Function
    },
    onClick: {
      type: Function
    },
    onEnter: {
      type: Function
    },
    sizeIcon: {
      type: String,
      default: '20'
    },
    hint: {
      type: String
    },
    persistentHint: {
      type: Boolean
    }
  },
  data() {
    return {
      error: false,
      formattedValue: null
    }
  },
  methods: {
    getValue() {
      if (this.value === 0) {
        this.formattedValue = this.value
        return
      }

      switch (this.type) {
        case 'number':
          this.formattedValue = this.formatValueToLocaleString(this.value)
          break
        default:
          this.formattedValue = this.value
          break
      }
    },
    formatValueToNumber(value) {
      const thousands = this.$constants.CURRENCY_CONFIG.thousands
      let valueNumberFormat = null
      if (value) {
        value = value.toString()
        valueNumberFormat =
          thousands === '.'
            ? parseFloat(value.replace(/\./g, ''))
            : parseFloat(value.replace(/,/g, ''))
      }
      return isNaN(valueNumberFormat) ? null : valueNumberFormat
    },
    formatValueToLocaleString(value) {
      let result
      const valueNumberFormat = this.formatValueToNumber(value)
      if (valueNumberFormat && !isNaN(valueNumberFormat)) {
        const cleanedValue = typeof value === 'string' ? valueNumberFormat : value
        if (this.formatNumber) {
          result = new Intl.NumberFormat('en-US').format(cleanedValue)
        } else {
          result = cleanedValue
        }
      }
      return result
    },
    handleError(value) {
      this.error = value
      this.$emit('onError', value, this.fieldName)
    },
    handleChange(value) {
      switch (this.type) {
        case 'number': {
          let emittedValue = this.formatValueToNumber(value)
          emittedValue = emittedValue === null ? '' : emittedValue.toString()
          this.$emit('onChange', emittedValue, this.fieldName)
          this.formattedValue = this.formatValueToLocaleString(value)
          break
        }
        default:
          this.$emit('onChange', value, this.fieldName)
          break
      }
    },
    // Click input box
    handleClick() {
      this.$emit('onClick')
    },
    handleEnter() {
      this.$emit('onEnter')
    },
    handleBlur() {
      this.$emit('onBlur', this.fieldName)
    },
    handleKeypress(e) {
      if (this.type === 'number') {
        const pattern = /^\d+$/
        const isNumber = pattern.test(e.key)
        if (!isNumber) {
          return e.preventDefault()
        }
      }
    }
  },
  mounted() {
    this.getValue()
  },
  watch: {
    value() {
      this.getValue()
    }
  }
}
</script>

<style lang="scss">
.a-text-field {
  // Disable style of vuetify
  &.v-input {
    padding: 0;
    margin: 0;
  }
  // End disable style of vuetify

  .v-input__slot {
    // Disable style of vuetify
    // remove border
    border: none !important;

    // remove border on hover
    &:hover,
    &:focus-within {
      border: none !important;
    }

    // remove border on focus
    &::before,
    &::after {
      display: none;
    }

    .v-input__prepend-inner {
      margin: 0;
    }
    // End disable style of vuetify

    // Update custom style
    height: 46px;
    background-color: #fff;
    display: flex;
    padding: 0 12px;
    border-radius: 4px;
    margin-bottom: 6px;

    .v-label {
      font-size: 16px !important;

      &.v-label--active {
        font-size: 16px !important;
      }
    }

    // Style for input
    .v-text-field__slot {
      input {
        padding: 6px 0;
        font-size: 16px;

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          display: none;
          -webkit-appearance: none;
          margin: 0;
        }

        &[type='number'] {
          -moz-appearance: textfield; /* Firefox */
        }
      }
    }

    .v-input__append-inner {
      margin: 0;
      height: 100%;
      display: flex;
      align-items: center;
    }
    //End update custom style
  }

  // Update custom style
  // Set color for icon if warning state
  .warning-icon {
    svg {
      path {
        fill: #ffab00;
      }
    }
  }

  &.dark {
    .v-input__slot {
      background-color: #f2f2f2;
    }
  }

  .v-text-field__details {
    padding-left: 12px;
  }

  &.v-input--is-focused,
  &.v-input--is-dirty {
    .v-input__slot {
      align-items: flex-end;
    }
  }

  &.solo {
    &.v-input--is-focused,
    &.v-input--is-dirty {
      .v-input__slot {
        align-items: center;
      }
    }
  }

  &.v-input--is-focused {
    .v-label {
      color: #ff8200 !important;

      &.error--text {
        color: #d44632 !important;
        caret-color: #d44632 !important;
      }
    }
  }

  &.error--text {
    color: #d44632 !important;
    caret-color: #d44632 !important;
  }

  @media only screen and (max-width: 600px) {
    .v-label {
      &.v-label--active {
        font-size: 14px !important;
      }
    }

    .v-text-field__slot {
      input {
        padding: 6px 0;
        font-size: 14px;

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          display: none;
          -webkit-appearance: none;
          margin: 0;
        }

        &[type='number'] {
          -moz-appearance: textfield; /* Firefox */
        }
      }
    }
  }
  //End update custom style
}
</style>
